import { clsx } from 'clsx';

import { Amount } from '@/components/common/Amount';
import { EditableCell } from '@/components/table/EditableCell';
import { SvgCheveronDown, SvgCheveronRight } from '@/components/icons';
import type { CategoryGroupMonth, CategoryMonth } from '@/api/types';
import { useBudgetStore } from '@/store/budget';
import { theme } from '@/style/theme';
import { currencyToAmount, DEFAULT_CURRENCY } from '@/utils/format';
import { makeAmountGrey, makeBalanceAmountStyle, tnum } from '@/utils/styles';

/**
 * Rows span every visible month: each month contributes a Budgeted / Spent /
 * Balance triple, matching the envelope grid in the demo screenshot.
 */

type CategoryRowProps = {
  columns: string;
  months: string[];
  cells: CategoryMonth[];
  name: string;
  groupId: string;
  isIncome: boolean;
};

export function BudgetCategoryRow({
  columns,
  months,
  cells,
  name,
  groupId,
  isIncome,
}: CategoryRowProps) {
  const setBudget = useBudgetStore(state => state.setBudget);

  return (
    <div
      className={clsx(
        'grid items-center border-b border-table-border bg-table-background text-sm',
        'hover:bg-table-row-background-hover',
      )}
      style={{ gridTemplateColumns: columns }}
      data-testid={`budget-row-${name}`}
    >
      <div
        className="truncate py-1.5 pr-2"
        style={{ paddingLeft: 26, color: theme.tableText }}
      >
        {name}
      </div>
      {months.map((month, index) => {
        const cell = cells[index];
        return (
          <div key={month} className="contents">
            {isIncome ? (
              <div className="px-2 py-1.5 text-right" style={tnum}>
                <Amount value={cell?.budgeted ?? 0} greyZero />
              </div>
            ) : (
              <EditableCell<number>
                value={cell?.budgeted ?? 0}
                align="right"
                className="m-1"
                placeholder="0.00"
                data-testid={`budgeted-${month}-${groupId}`}
                parse={text => currencyToAmount(text, DEFAULT_CURRENCY)}
                render={value => (
                  <span style={makeAmountGrey(value)}>
                    <Amount value={value} />
                  </span>
                )}
                onCommit={next => void setBudget(month, groupId, next)}
              />
            )}
            <div className="px-2 py-1.5 text-right">
              <Amount value={cell?.spent ?? 0} greyZero />
            </div>
            <div
              className="px-2 py-1.5 text-right"
              style={{ ...tnum, ...makeBalanceAmountStyle(cell?.balance ?? 0) }}
            >
              <Amount value={cell?.balance ?? 0} greyZero />
            </div>
          </div>
        );
      })}
    </div>
  );
}

type GroupRowProps = {
  columns: string;
  months: string[];
  cells: CategoryGroupMonth[];
  name: string;
  groupId: string;
  isIncome: boolean;
  categoryCells: Map<string, CategoryMonth[]>;
};

export function BudgetGroupRow({
  columns,
  months,
  cells,
  name,
  groupId,
  isIncome,
  categoryCells,
}: GroupRowProps) {
  const collapsed = useBudgetStore(state =>
    state.collapsedGroups.includes(groupId),
  );
  const toggleGroup = useBudgetStore(state => state.toggleGroup);

  const first = cells[0];

  return (
    <>
      <div
        className="grid items-center border-b border-table-border bg-table-row-header-background text-sm font-semibold"
        style={{ gridTemplateColumns: columns }}
        data-testid={`budget-group-${name}`}
      >
        <button
          type="button"
          onClick={() => toggleGroup(groupId)}
          className="flex min-w-0 items-center gap-1 py-2 pl-2.5 pr-2 text-left text-table-row-header-text"
        >
          {collapsed ? (
            <SvgCheveronRight width={12} height={12} />
          ) : (
            <SvgCheveronDown width={12} height={12} />
          )}
          <span className="truncate">{name}</span>
        </button>
        {months.map((month, index) => {
          const cell = cells[index];
          return (
            <div key={month} className="contents">
              <div className="px-2 py-2 text-right" style={tnum}>
                <Amount value={cell?.budgeted ?? 0} />
              </div>
              <div className="px-2 py-2 text-right" style={tnum}>
                <Amount value={isIncome ? 0 : (cell?.spent ?? 0)} greyZero />
              </div>
              <div
                className="px-2 py-2 text-right"
                style={{
                  ...tnum,
                  ...makeBalanceAmountStyle(cell?.balance ?? 0),
                }}
              >
                <Amount value={cell?.balance ?? 0} />
              </div>
            </div>
          );
        })}
      </div>
      {!collapsed &&
        first?.categories.map(category => (
          <BudgetCategoryRow
            key={category.id}
            columns={columns}
            months={months}
            name={category.name}
            groupId={category.id}
            isIncome={isIncome}
            cells={categoryCells.get(category.id) ?? []}
          />
        ))}
    </>
  );
}
